import { useMemo, useRef } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../../utils/modalStyles";
import { ConfigFormAntdCss } from "../../panel/formParts";
import { CloseIcon, PencilIcon } from "./deviceModalIcons";

interface Props {
  open: boolean;
  // Всички пътища за затваряне (X, „Отказ", фонът) — през пазача за
  // незапазени промени.
  onRequestClose: () => void;
  icon: React.ReactNode;
  kicker: string;
  // По-краткият надпис над името на телефон.
  phoneKicker: string;
  name: string;
  onNameChange: (name: string) => void;
  // При създаване курсорът е направо в името.
  autoFocusName: boolean;
  // Разделите под хедъра — само на телефон.
  phoneTabs?: React.ReactNode;
  // Обобщението вляво във футъра — без телефон.
  summary?: React.ReactNode;
  saveLabel: string;
  canSave: boolean;
  onSave: () => void;
  // Мрежата на секциите (Tailwind клас); по подразбиране — по 480px колона.
  gridClassName?: string;
  // „compact" — една секция (терминал): до 760px и без разпъване на цяла
  // височина на таблет; „wide" — секции една до друга (POS/фискален).
  size?: "wide" | "compact";
  // Секциите — една до друга, докато се събират в мрежата, иначе една под друга.
  children: React.ReactNode;
}

const DEFAULT_GRID_CLASS = "grid-cols-[repeat(auto-fit,minmax(min(480px,100%),1fr))]";

// Ръбът на модала до екрана на таблет/десктоп; на телефон е на цял екран.
const DESKTOP_WIDTH = "min(1280px, calc(100vw - 64px))";
const TABLET_WIDTH = "calc(100vw - 32px)";
const COMPACT_WIDTH = "min(760px, calc(100vw - 32px))";

/**
 * Общата рамка на модалите за устройство (POS и фискален): хедър с вида и
 * името (редактира се на място), тяло със секциите, футър с обобщение и
 * „Отказ / Запази". На телефон модалът е на цял екран.
 */
const DeviceModalShell = ({
  open,
  onRequestClose,
  icon,
  kicker,
  phoneKicker,
  name,
  onNameChange,
  autoFocusName,
  phoneTabs,
  summary,
  saveLabel,
  canSave,
  onSave,
  gridClassName = DEFAULT_GRID_CLASS,
  size = "wide",
  children,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Стабилна референция — Modal не бива да се прерисува при всяка буква от
  // името. На телефон модалът е на цял екран, без закръгляне.
  const shellStyles = useMemo(
    () => ({
      ...modalStyles,
      content: { ...modalStyles.content, borderRadius: isMobile ? 0 : 16 },
    }),
    [isMobile],
  );

  return (
    <Modal
      open={open}
      onCancel={onRequestClose}
      // Esc го поема пазачът за незапазени промени (useUnsavedChangesGuard).
      keyboard={false}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={
        isMobile ? "100vw" : size === "compact" ? COMPACT_WIDTH : isTablet ? TABLET_WIDTH : DESKTOP_WIDTH
      }
      styles={shellStyles}
      style={isMobile ? { top: 0, margin: 0, padding: 0, maxWidth: "100vw" } : modalWrapStyle}
    >
      <ConfigFormAntdCss />
      <div
        className={`flex flex-col bg-surface ${
          isMobile
            ? "h-dvh"
            : isTablet && size === "wide"
              ? "h-[calc(100dvh-32px)]"
              : "max-h-[calc(100dvh-64px)]"
        }`}
      >
        {/* Хедър: вид + име (редактира се на място) + затваряне */}
        <div
          // На телефон долната линия е под разделите, не под хедъра.
          className={`flex shrink-0 items-center bg-surface ${
            isNarrow ? "gap-2 px-3 py-2.5" : "gap-3.5 border-b border-shade-2 px-6 py-[18px]"
          }`}
        >
          {!isNarrow && (
            <span className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[10px] bg-brand-tint text-brand-deep-ink">
              {icon}
            </span>
          )}
          <div className="flex min-w-0 max-w-[420px] flex-1 flex-col gap-0.5">
            <div className="truncate text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1.4px] text-muted">
              {isNarrow ? phoneKicker : kicker}
            </div>
            <div className="flex max-w-full items-center">
              <input
                ref={nameInputRef}
                value={name}
                onChange={(event) => {
                  const value = event.target.value;
                  onNameChange(value.charAt(0).toUpperCase() + value.slice(1));
                }}
                aria-label={t("devices.posModal.nameAria")}
                placeholder={t("devices.posModal.namePlaceholder")}
                size={Math.max(8, name.length + 1)}
                autoFocus={autoFocusName}
                className="h-[34px] min-w-12 max-w-full rounded-[8px] border border-transparent bg-transparent px-0 text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink outline-none [field-sizing:content] placeholder:text-faint hover:border-shade-3 focus:border-brand focus:bg-surface focus:shadow-[0_0_0_2px_var(--color-brand-tint2)]"
              />
              {/* Моливчето отваря полето за име — курсорът отива в края. */}
              <button
                type="button"
                onClick={() => {
                  const input = nameInputRef.current;
                  if (!input) return;
                  input.focus();
                  input.setSelectionRange(input.value.length, input.value.length);
                }}
                aria-label={t("devices.posModal.editName")}
                className="ml-0.5 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-[6px] text-muted hover:bg-shade-1 hover:text-ink"
              >
                <PencilIcon size={14} />
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={onRequestClose}
            aria-label={t("devices.posModal.close")}
            className="ml-auto flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[8px] text-muted hover:bg-shade-1 hover:text-ink"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {isNarrow && phoneTabs}

        {/* Тяло: секциите — една до друга, докато се събират в мрежата. */}
        <div
          className={`cfg-form min-h-0 flex-1 overflow-y-auto bg-canvas ${
            isNarrow ? "p-3" : isTablet ? "p-4" : "px-6 py-5"
          }`}
        >
          <div
            className={`grid items-start ${gridClassName} ${
              isNarrow ? "gap-3" : "gap-4"
            }`}
          >
            {children}
          </div>
        </div>

        {/* Футър: обобщение (без телефон) + Отказ / Запази */}
        <div
          className={`flex shrink-0 items-center gap-2.5 border-t border-shade-2 bg-surface ${
            isNarrow
              ? "px-3 pt-2.5 pb-[calc(10px+env(safe-area-inset-bottom,0px)*0.5)]"
              : "px-6 py-3.5"
          }`}
        >
          {/* Обобщението пада на два реда, когато не се събира — бутоните
              остават вдясно. */}
          {!isNarrow && summary && (
            <div className="mr-auto flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-0.5 text-[12.5px] text-body">
              {summary}
            </div>
          )}
          <button
            type="button"
            onClick={onRequestClose}
            className={`h-11 cursor-pointer rounded-[10px] border border-shade-3 bg-surface px-5 text-[14px] font-semibold text-ink-2 hover:bg-fill ${
              isNarrow ? "flex-1" : "ml-auto flex-none"
            }`}
          >
            {t("devices.posModal.cancel")}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className={`h-11 cursor-pointer rounded-[10px] bg-brand px-6 text-[14px] font-bold text-white hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-brand ${
              isNarrow ? "flex-[2]" : "flex-none"
            }`}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Ред от обобщението във футъра: цветна точка, етикет, стойност.
export const DeviceSummaryItem = ({
  dotClassName,
  label,
  value,
  isTruncated = false,
}: {
  dotClassName: string;
  label: string;
  value: string;
  // Дългата стойност (напр. списък станции) се реже, вместо да избутва бутоните.
  isTruncated?: boolean;
}) => (
  <span
    className={`flex items-center gap-1.5 whitespace-nowrap ${
      isTruncated ? "min-w-0 max-w-full" : "shrink-0"
    }`}
  >
    <span className={`h-2 w-2 shrink-0 rounded-full ${dotClassName}`} />
    {label}
    <b className={`text-ink ${isTruncated ? "truncate" : ""}`}>{value}</b>
  </span>
);

export default DeviceModalShell;
